import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tripsApi } from '../../../api/trips.api';
import type { Invitation } from '../../../api/invitations.api';
import type { TripParticipant } from '../../../types';
import { toast } from '../../ui/Toast';
import Spinner from '../../ui/Spinner';
import ConfirmModal from '../../ui/ConfirmModal';
import { UserAvatar } from '../../ui/UserAvatar';
import BirthDateInput from '../../ui/BirthDateInput';

interface MergeTarget { userId: string; name: string }

interface Props {
  tripId: string;
  participant: TripParticipant;
  isOwner: boolean;
  /** ADR-0029: editors write a placeholder's date of birth — they have no account to do it. */
  canEdit: boolean;
  /** Real accounts already in the trip, for "merge into". */
  mergeTargets: MergeTarget[];
  onInvited: (invitation: Invitation) => void;
  onRemove: (userId: string) => void;
  onChanged: () => void;
  /** ADR-0033: the lead traveller of an agency's trip owns it — linked, never removed. */
  ownsTrip?: boolean;
}

/**
 * ADR-0021 — a traveller without an account. No role (they can't sign in); the
 * owner links them to a real account, by invitation or by merging into someone
 * already in the trip.
 */
export default function PlaceholderRow({ tripId, participant, isOwner, canEdit, mergeTargets, onInvited, onRemove, onChanged, ownsTrip = false }: Props) {
  const { t, i18n } = useTranslation();
  const name = participant.user.displayName || participant.user.username;
  const [linking, setLinking] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'EDITOR' | 'VIEWER'>('EDITOR');
  const [inviting, setInviting] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [merging, setMerging] = useState(false);
  const target = mergeTargets.find(m => m.userId === targetId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingBirth, setEditingBirth] = useState(false);

  async function handleBirthDate(birthDate: string) {
    if (!birthDate || birthDate === participant.birthDate) return;
    try {
      await tripsApi.setPlaceholderBirthDate(tripId, participant.userId, birthDate);
      setEditingBirth(false);
      onChanged();
    } catch {
      toast(t('trip.participants.placeholder.toast.birthDateFailed'), 'error');
    }
  }

  async function handleInvite() {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return;
    setInviting(true);
    try {
      const invitation = await tripsApi.invitePlaceholder(tripId, participant.userId, normalized, role, i18n.language);
      onInvited(invitation);
      setEmail('');
      setLinking(false);
      toast(t('trip.participants.toast.invited'), 'success');
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast(code === 'EMAIL_NOT_VERIFIED' ? t('trip.participants.toast.emailNotVerified') : t('trip.participants.toast.inviteFailed'), 'error');
    } finally {
      setInviting(false);
    }
  }

  async function handleMerge() {
    if (!target) return;
    setMerging(true);
    try {
      await tripsApi.mergePlaceholder(tripId, participant.userId, target.userId);
      toast(t('trip.participants.placeholder.toast.merged'), 'success');
      setConfirmOpen(false);
      onChanged();
    } catch {
      toast(t('trip.participants.placeholder.toast.mergeFailed'), 'error');
    } finally {
      setMerging(false);
    }
  }

  return (
    <div className="py-2 border-t border-slate-100 dark:border-slate-700/60">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <UserAvatar name={name} size={36} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{name}</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {t('trip.participants.placeholder.noAccount')}
              {participant.ageAtTripStart != null && ` · ${t('trip.participants.age', { count: participant.ageAtTripStart })}`}
            </p>
            {canEdit && (
              <button type="button" onClick={() => setEditingBirth(v => !v)} className="block text-xs text-left text-teal-600 dark:text-teal-400 hover:underline cursor-pointer">
                {participant.birthDate ? t('trip.participants.placeholder.editBirthDate') : t('trip.participants.placeholder.addBirthDate')}
              </button>
            )}
          </div>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setLinking(v => !v)} className="btn-ghost text-xs">
              {t('trip.participants.placeholder.link')}
            </button>
            {!ownsTrip && (
              <button onClick={() => onRemove(participant.userId)} className="btn-ghost text-red-400 text-xs">
                {t('trip.participants.remove')}
              </button>
            )}
          </div>
        )}
      </div>

      {editingBirth && (
        <div className="mt-3 ml-12">
          <BirthDateInput value={participant.birthDate ?? ''} onChange={handleBirthDate} />
        </div>
      )}

      {linking && (
        <div className="mt-3 ml-12 space-y-3">
          <div className="space-y-2">
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{t('trip.participants.placeholder.inviteHint')}</p>
            <input
              type="email"
              className="input w-full"
              placeholder="amigo@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleInvite()}
            />
            <div className="flex gap-2">
              <select className="input flex-1 min-w-0" value={role} onChange={e => setRole(e.target.value as 'EDITOR' | 'VIEWER')}>
                <option value="EDITOR">{t('trip.participants.roleEditor')}</option>
                <option value="VIEWER">{t('trip.participants.roleViewer')}</option>
              </select>
              <button onClick={handleInvite} className="btn-primary shrink-0" disabled={inviting || !email.trim()}>
                {inviting ? <Spinner size="sm" /> : t('trip.participants.sendInvitation')}
              </button>
            </div>
          </div>

          {mergeTargets.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-500 dark:text-slate-400">{t('trip.participants.placeholder.mergeHint')}</p>
              <div className="flex gap-2">
                <select className="input flex-1 min-w-0" value={targetId} onChange={e => setTargetId(e.target.value)}>
                  <option value="">{t('trip.participants.placeholder.mergeSelect')}</option>
                  {mergeTargets.map(m => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </select>
                <button onClick={() => setConfirmOpen(true)} className="btn-secondary shrink-0" disabled={!target}>
                  {t('trip.participants.placeholder.merge')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmOpen && !!target}
        title={t('trip.participants.placeholder.confirmMergeTitle', { name, target: target?.name ?? '' })}
        body={t('trip.participants.placeholder.confirmMergeBody', { name, target: target?.name ?? '' })}
        confirmLabel={t('trip.participants.placeholder.merge')}
        onConfirm={handleMerge}
        onCancel={() => setConfirmOpen(false)}
        loading={merging}
      />
    </div>
  );
}
